import React, { useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Paragraph, Button, colors, H3 } from '@fs/zion-ui'
import { useQueryParams, useLocation } from '@fs/zion-router'
import { useErrorStatus } from '../../views/imageBrowse/hooks'
import { errorStates } from '../../views/imageBrowse/helpers/errorStates'
import unauthorizedSvg from './images/unauthorized.svg'
import unavailableSvg from './images/unavailable.svg'

const wrapperCss = css`
  background-color: ${colors.gray03};
  flex: 1;
`
const inViewerWrapperCss = css`
  background-color: ${colors.gray03};
  height: 100%;
  width: 100%;
`
const contentCss = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  max-width: 750px;
  margin-inline-start: auto;
  margin-inline-end: auto;
  text-align: center;
  gap: 4px;
  height: 100%;
`
const imgCss = css`
  max-width: 100%;
  height: auto;
  margin-top: 16px;
`

const referrers = {
  EXPLORE_IMAGES_SEARCH: 0,
  RECORD_DETAILS: 1,
  OTHER: 2,
}

function ImageGroupError({ errorState, view }) {
  const [t] = useTranslation()
  const { setErrorStatus } = useErrorStatus()
  const containerRef = useRef()
  const inComboViewer = !view
  const { query } = useQueryParams()
  // Set when the user switched to single-item view; lets us return to group view.
  const returnUrl = useLocation().state?.imageGroupErrorReturnUrl

  useEffect(() => {
    if (errorState && inComboViewer) setErrorStatus(errorState)
  }, [errorState, inComboViewer, setErrorStatus])

  const referrer = useMemo(() => {
    if (inComboViewer) return null
    const lastUrl = document.referrer
    if (lastUrl.includes('images/search-results') || lastUrl.includes('images/beta/search-results')) {
      return referrers.EXPLORE_IMAGES_SEARCH
    }
    if (lastUrl.includes('ark:/61903/1:1') || lastUrl.includes('ark:/61903/1:2')) {
      return referrers.RECORD_DETAILS
    }
    return referrers.OTHER
  }, [inComboViewer])

  const message = useMemo(() => {
    let heading
    let body
    if (errorState === errorStates.UNAUTHORIZED) {
      if (query.volumeSetSearch === 'jiapu') {
        body = t(
          'ImageGroupError.jiapuUnauthorizedBody',
          'FamilySearch International has recently suspended access to certain Chinese family records as it reviews developments with respect to privacy laws, contractual agreements and technology requirements. Thank you for your understanding and continued support.'
        )
      } else {
        body = t(
          'ImageGroupError.unauthorizedBody',
          "We don't have permission to share this image group. Access is usually determined by the records' creator or custodian or by data privacy laws."
        )
      }
      heading = t('ImageGroupError.unauthorizedHeader', 'Unable to Display Image Group')
    } else if (errorState === errorStates.NOT_FOUND) {
      heading = t('ImageGroupError.notFoundHeader', 'Image Group Not Found')
      body = t('ImageGroupError.notFoundBody', 'We are unable to find this image group.')
    } else {
      heading = t('ImageGroupError.unavailableHeader', 'Cannot Display Image Group')
      body = t('ImageGroupError.unavailableBody', 'Try refreshing the page, or come back later.')
    }
    return (
      <>
        <H3>{heading}</H3>
        <Paragraph>{body}</Paragraph>
      </>
    )
  }, [errorState, t, query.volumeSetSearch])

  const button = useMemo(() => {
    if (!(view === 'explore' || view === 'organize')) return null

    if (returnUrl) {
      return (
        <Button emphasis="medium" to={returnUrl}>
          {t('common-ui.back.action')}
        </Button>
      )
    }

    // gives users the option to view the image group in the non-react version when timeouts happen
    // we'll use this until we resolve timeout issues
    // this won't opt users out of the new experience
    if (errorState !== errorStates.UNAVAILABLE) {
      const path = referrer === referrers.OTHER ? '/records/images' : document.referrer
      let text
      if (referrer === referrers.OTHER) text = t('ImageGroupError.goToExplore', 'Go To Explore Images')
      else if (referrer === referrers.EXPLORE_IMAGES_SEARCH) text = t('ImageGroupError.backToSearch', 'Back To Search')
      else text = t('common-ui.back.action')
      return (
        <Button emphasis="medium" external to={path}>
          {text}
        </Button>
      )
    }
    return null
  }, [errorState, referrer, t, view, returnUrl])

  return (
    <div css={inComboViewer ? inViewerWrapperCss : wrapperCss} ref={containerRef}>
      <div css={contentCss}>
        {message}
        {button}
        <img
          alt=""
          aria-hidden
          src={errorState === errorStates.UNAUTHORIZED ? unauthorizedSvg : unavailableSvg}
          css={imgCss}
        />
      </div>
    </div>
  )
}

ImageGroupError.propTypes = {
  errorState: PropTypes.oneOf(Object.values(errorStates)).isRequired,
  view: PropTypes.oneOf(['index', 'explore', 'organize']),
}

export default React.memo(ImageGroupError)
